import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { isDefined, useIsModalActive } from '#shared';

import { useSelectIsRotationOn } from '../../../widgetManager';
import { useHighlightStatus } from '../../useHighlightStatus';
import { useIsIdle } from '../../useIsIdle';

import {
  AppBehaviorEventEmitter,
  CaseLoopEndEvent,
  CaseLoopNextEvent,
  CaseLoopStartEvent,
  CaseRotationUpdateEvent,
  UserIdleEndEvent,
  UserIdleStartEvent,
} from './appBehaviorEventEmitter';

import type { CaseRotationStatus } from '#entities';

type Orientation = 'horizontal' | 'vertical';

export type AppBehaviorContextProps = {
  onUserIdleStart: (
    listener: (event: CustomEvent<UserIdleStartEvent>) => void,
  ) => () => void;
  onUserIdleEnd: (
    listener: (event: CustomEvent<UserIdleEndEvent>) => void,
  ) => () => void;
  onCaseLoopStart: (
    listener: (event: CustomEvent<CaseLoopStartEvent>) => void,
  ) => () => void;
  onCaseLoopNext: (
    listener: (event: CustomEvent<CaseLoopNextEvent>) => void,
  ) => () => void;
  onCaseLoopEnd: (
    listener: (event: CustomEvent<CaseLoopEndEvent>) => void,
  ) => () => void;
  onCaseRotationUpdate: (
    listener: (event: CustomEvent<CaseRotationUpdateEvent>) => void,
  ) => () => void;
  behaviorEventEmitter: AppBehaviorEventEmitter;

  // actions for StatusBar/Rotation
  toggleStatus: (s: CaseRotationStatus) => void;
  clearHighlightSelection: () => void;
  onSearchInput: () => void;
  selectedStatus: CaseRotationStatus | null;
  highlightStatus?: CaseRotationStatus;
  isManualHighlight: boolean;
  isAutoHighlight: boolean;
  shouldApplyHighlight: boolean;
};

export const AppBehaviorContext = createContext<
  AppBehaviorContextProps | undefined
>(undefined);

export const useAppBehavior = (): AppBehaviorContextProps => {
  const ctx = useContext(AppBehaviorContext);
  if (!ctx) {
    throw new Error(
      'useAppBehavior must be used within an AppBehaviorProvider',
    );
  }
  return ctx;
};

export const AppBehaviorProvider = ({ children }: PropsWithChildren) => {
  const dirRef = useRef<1 | -1>(1);
  const canMoveRef = useRef<Map<string, boolean>>(new Map());
  const edgeFlagsRef = useRef<Map<string, 1 | -1 | null>>(new Map());
  const pausedRef = useRef(false);

  const startedRef = useRef(false); // start ticking logic only after first loop tick arrives
  const phaseRef = useRef<Orientation>('horizontal');
  const idToOrientationRef = useRef<Map<string, Orientation>>(new Map());
  const completedPhasesRef = useRef<Set<Orientation>>(new Set());

  const behaviorEventEmitter = useMemo(() => new AppBehaviorEventEmitter(), []);
  // Event listener convenience hooks
  const onUserIdleStart = useCallback(
    (listener: (event: CustomEvent<UserIdleStartEvent>) => void) =>
      behaviorEventEmitter.addUserIdleStartListener(listener),
    [behaviorEventEmitter],
  );

  const onUserIdleEnd = useCallback(
    (listener: (event: CustomEvent<UserIdleEndEvent>) => void) =>
      behaviorEventEmitter.addUserIdleEndListener(listener),
    [behaviorEventEmitter],
  );

  const onCaseLoopStart = useCallback(
    (listener: (event: CustomEvent<CaseLoopStartEvent>) => void) =>
      behaviorEventEmitter.addCaseLoopStartListener(listener),
    [behaviorEventEmitter],
  );

  const onCaseLoopNext = useCallback(
    (listener: (event: CustomEvent<CaseLoopNextEvent>) => void) =>
      behaviorEventEmitter.addCaseLoopNextListener(listener),
    [behaviorEventEmitter],
  );
  const onCaseLoopEnd = useCallback(
    (listener: (event: CustomEvent<CaseLoopEndEvent>) => void) =>
      behaviorEventEmitter.addCaseLoopEndListener(listener),
    [behaviorEventEmitter],
  );

  const onCaseRotationUpdate = useCallback(
    (listener: (event: CustomEvent<CaseRotationUpdateEvent>) => void) =>
      behaviorEventEmitter.addCaseRotationUpdateListener(listener),
    [behaviorEventEmitter],
  );

  const isRotationOn = useSelectIsRotationOn();
  const isIdle = useIsIdle();
  const isModalActive = useIsModalActive();

  const {
    highlightStatus,
    selectedManually,
    toggleStatus,
    clearSelection: clearHighlightSelection,
    onSearchInput,
  } = useHighlightStatus();

  // Auto is active when enabled + idle, manual is active when selected != null
  const isAutoHighlightActive = isRotationOn && isIdle && !isModalActive;
  const isManualHighlightActive = isDefined(selectedManually);
  const shouldApplyHighlight = isManualHighlightActive || isAutoHighlightActive;

  // helpers for phase mode
  const activeIdsForPhase = useCallback(
    (phase: Orientation) =>
      [...canMoveRef.current.entries()]
        .filter(
          ([id, canMove]) =>
            canMove && idToOrientationRef.current.get(id) === phase,
        )
        .map(([id]) => id),
    [],
  );

  const clearFlagsForPhase = useCallback(
    (phase: Orientation) => {
      for (const id of activeIdsForPhase(phase)) {
        edgeFlagsRef.current.set(id, null);
      }
    },
    [activeIdsForPhase],
  );

  const allAtEdgeForPhase = useCallback(
    (phase: Orientation, dir: 1 | -1) => {
      const active = activeIdsForPhase(phase);
      if (active.length === 0) {
        return true;
      } // treat empty phase as done
      return active.every(id => edgeFlagsRef.current.get(id) === dir);
    },
    [activeIdsForPhase],
  );

  const switchPhase = useCallback(() => {
    phaseRef.current =
      phaseRef.current === 'horizontal' ? 'vertical' : 'horizontal';
    clearFlagsForPhase(phaseRef.current);
  }, [clearFlagsForPhase]);

  const maybeAdvancePhaseOrFlip = useCallback(() => {
    const phase = phaseRef.current;
    if (!allAtEdgeForPhase(phase, dirRef.current)) {
      return;
    }

    completedPhasesRef.current.add(phase);

    // both phases done → flip dir, reset
    if (
      completedPhasesRef.current.has('horizontal') &&
      completedPhasesRef.current.has('vertical')
    ) {
      dirRef.current = dirRef.current === 1 ? -1 : 1;
      completedPhasesRef.current.clear();
    }

    switchPhase();
  }, [allAtEdgeForPhase, switchPhase]);

  useEffect(() => {
    if (!highlightStatus) {
      return;
    }
    behaviorEventEmitter.emit({
      type: 'case-rotation-update',
      value: highlightStatus,
    });
  }, [highlightStatus, isManualHighlightActive, behaviorEventEmitter]);

  // boot timers
  useEffect(() => {
    const idleTimeout = setTimeout(() => {
      behaviorEventEmitter.emit({ type: 'user-idle-start' });
    }, 30_000);

    const caseLoopInterval = setInterval(() => {
      behaviorEventEmitter.emit({ type: 'case-loop-next' });
    }, 5000);

    const unsubEnd = onCaseLoopEnd(() => clearInterval(caseLoopInterval));

    return () => {
      behaviorEventEmitter.emit({ type: 'case-loop-end' });
      behaviorEventEmitter.clear();
      clearTimeout(idleTimeout);
      clearInterval(caseLoopInterval);
      unsubEnd();
    };
  }, [behaviorEventEmitter, onCaseLoopEnd]);

  useEffect(() => {
    pausedRef.current = !isIdle;
  }, [isIdle]);

  // bus listeners
  useEffect(() => {
    const uReg = behaviorEventEmitter.addListener('carousel-register', e => {
      const { id, canMove, variant } = e.detail as {
        id: string;
        canMove: boolean;
        variant?: Orientation;
      };
      canMoveRef.current.set(id, canMove);
      edgeFlagsRef.current.set(id, null);
      if (variant) {
        idToOrientationRef.current.set(id, variant);
      }
    });

    const uCan = behaviorEventEmitter.addListener(
      'carousel-update-can-move',
      e => {
        const { id, canMove } = e.detail;
        canMoveRef.current.set(id, canMove);
        edgeFlagsRef.current.set(id, null);
      },
    );
    const uEdge = behaviorEventEmitter.addListener(
      'carousel-report-edge',
      e => {
        const { id, dir } = e.detail as { id: string; dir: 1 | -1 };
        if (dir !== dirRef.current) {
          return;
        }
        if (!canMoveRef.current.get(id)) {
          return;
        }

        const o = idToOrientationRef.current.get(id);
        if (!o || o !== phaseRef.current) {
          return;
        }

        edgeFlagsRef.current.set(id, dir);
        maybeAdvancePhaseOrFlip();
      },
    );

    const uForce = behaviorEventEmitter.addListener(
      'carousel-force-direction',
      e => {
        const ndir = e.detail.dir;
        if (ndir !== dirRef.current) {
          dirRef.current = ndir;
          completedPhasesRef.current.clear();
          clearFlagsForPhase('horizontal');
          clearFlagsForPhase('vertical');
        }
      },
    );

    return () => {
      uReg();
      uCan();
      uEdge();
      uForce();
    };
  }, [behaviorEventEmitter, clearFlagsForPhase, maybeAdvancePhaseOrFlip]);

  // emit ticks ONLY for current phase
  useEffect(
    () =>
      onCaseLoopNext(() => {
        if (pausedRef.current) {
          return;
        }

        if (!startedRef.current) {
          startedRef.current = true; // avoids any pre-start phase churn
        }

        const target = phaseRef.current;

        // if current phase empty now, advance (but only here, not during register)
        if (activeIdsForPhase(target).length === 0) {
          completedPhasesRef.current.add(target);
          switchPhase();
          return;
        }

        behaviorEventEmitter.emit({
          type: 'carousel-tick',
          dir: dirRef.current,
          targetOrientation: target,
        });
      }),
    [onCaseLoopNext, behaviorEventEmitter, activeIdsForPhase, switchPhase],
  );

  const providerValue = useMemo(
    () => ({
      onUserIdleStart,
      onUserIdleEnd,
      onCaseLoopStart,
      onCaseLoopNext,
      onCaseLoopEnd,
      behaviorEventEmitter,
      onCaseRotationUpdate,
      toggleStatus,
      clearHighlightSelection,
      onSearchInput,
      highlightStatus,
      selectedStatus: selectedManually,
      isManualHighlight: isManualHighlightActive,
      isAutoHighlight: isAutoHighlightActive,
      shouldApplyHighlight,
    }),
    [
      onUserIdleStart,
      onUserIdleEnd,
      onCaseLoopStart,
      onCaseLoopNext,
      onCaseLoopEnd,
      behaviorEventEmitter,
      onCaseRotationUpdate,
      toggleStatus,
      clearHighlightSelection,
      onSearchInput,
      selectedManually,
      highlightStatus,
      isManualHighlightActive,
      isAutoHighlightActive,
      shouldApplyHighlight,
    ],
  );

  return (
    <AppBehaviorContext.Provider value={providerValue}>
      {children}
    </AppBehaviorContext.Provider>
  );
};
